import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import "../mental-clarity-guide.css";

const DEFAULT_TITLE = "Ahavibes — Stop reacting. Start thinking clearly.";
const DEFAULT_DESCRIPTION =
  "A guided framework to examine interpretations, reduce mental noise, and make better decisions.";

const PAGE_TITLE = "Ahavibes Guide — Five Pillars for Better Thinking";
const PAGE_DESCRIPTION =
  "Explore the five pillars of the Ahavibes guide system: mental clarity, mental noise, structured thinking, emotional awareness, and better decisions.";

const PILLARS = [
  {
    number: 1,
    title: "Mental Clarity",
    path: "/mental-clarity",
    description:
      "The ability to distinguish facts from assumptions and make thoughtful decisions.",
    active: true,
  },
  {
    number: 2,
    title: "Mental Noise",
    path: "/mental-noise",
    description:
      "Understanding the hidden conversations that shape your decisions.",
    active: true,
  },
  {
    number: 3,
    title: "Structured Thinking",
    path: "/structured-thinking",
    description:
      "Frameworks to organize thoughts and make better decisions.",
    active: true,
  },
  {
    number: 4,
    title: "Emotional Awareness",
    path: "/emotional-awareness",
    description:
      "Naming emotions and choosing deliberate responses without suppressing them.",
    active: true,
  },
  {
    number: 5,
    title: "Better Decisions",
    path: "/better-decisions",
    description:
      "Turning clarity, awareness, and structure into confident action.",
    active: true,
  },
];

const GuideHub = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;

    let meta = document.querySelector('meta[name="description"]');
    const createdMeta = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute("content");
    meta.setAttribute("content", PAGE_DESCRIPTION);

    return () => {
      document.title = DEFAULT_TITLE;
      if (createdMeta) {
        meta.remove();
      } else {
        meta.setAttribute("content", previousDescription || DEFAULT_DESCRIPTION);
      }
    };
  }, []);

  return (
    <PageContainer size="wide" className="mental-clarity-guide">
      <div className="guide-hub">
        <header className="guide-hub__hero">
          <h1 className="guide-hub__title">Five Pillars for Better Thinking</h1>
          <p className="guide-hub__lead">
            A guided framework to examine interpretations, reduce mental noise, and
            make better decisions. Explore each pillar at your own pace.
          </p>
        </header>

        <div className="guide-hub__grid">
          {PILLARS.map((pillar) => {
            const cardClass = `guide-hub__card${
              pillar.active ? "" : " guide-hub__card--soon"
            }`;

            const cardContent = (
              <>
                <span className="guide-hub__card-number">
                  Pillar {pillar.number}
                </span>
                <h2 className="guide-hub__card-title">{pillar.title}</h2>
                <p className="guide-hub__card-desc">{pillar.description}</p>
                {!pillar.active && (
                  <span className="guide-hub__card-badge">Coming Soon</span>
                )}
              </>
            );

            if (pillar.active) {
              return (
                <Link
                  key={pillar.number}
                  to={pillar.path}
                  className={cardClass}
                >
                  {cardContent}
                </Link>
              );
            }

            return (
              <div key={pillar.number} className={cardClass}>
                {cardContent}
              </div>
            );
          })}
        </div>

        <aside className="guide-hub__aside">
          <Link to="/guide/common-questions" className="guide-hub__aside-link">
            <span className="guide-hub__aside-eyebrow">Also in this guide</span>
            <span className="guide-hub__aside-title">
              Common Questions We Know but Never Ask
            </span>
            <p className="guide-hub__aside-desc">
              The things your mind already suspects — just never says out loud. Short
              answers on overthinking, clarity, and better decisions.
            </p>
          </Link>
        </aside>
      </div>
    </PageContainer>
  );
};

export default GuideHub;
